/**
 * Die Felder eines Antrags in zwei Abschnitten – Antrag und Eckdaten –, in den Stammdaten
 * zusätzlich Verfahren.
 *
 * Dieselben Abschnitte erscheinen beim Anlegen (`VerfahrenFormular`) und in den Stammdaten des
 * geöffneten Antrags (`AntragStammdaten`). Es gibt sie bewusst nur einmal: Jedes Feld steht an
 * beiden Stellen am selben Platz, die Stammdaten ergänzen, was man erst später pflegt.
 *
 * | Abschnitt  | Anlegen und Stammdaten                                    | nur in den Stammdaten |
 * |------------|-----------------------------------------------------------|-----------------------|
 * | Antrag     | Bezeichnung, Bearbeitung, Geplante Genehmigung            | Bezugsantrag, Beschreibung, Bemerkung zum Zusatzantrag (nur bei Teilgenehmigung / vorzeitigem Beginn) |
 * | (Antrag)   | Antragskonferenz am (freiwillig)                          | Unterabschnitt Antragskonferenz (`AntragKonferenz`, als `konferenz`) |
 * | Eckdaten   | Anlagen, Umfang, UVP-Status, Windenergiegebiet, Besonderheiten | –                |
 * | Verfahren  | –                                                          | Eingereicht am, Vollständig seit, Bescheid vom, Aktenzeichen |
 *
 * Die Antragsart ist kein Feld: Beim Anlegen wird sie vorab als Kachel gewählt
 * (`ProcedureTypeTiles`), danach steht sie nur noch im Titel.
 *
 * Beim Anlegen stehen die Abschnitte als Karten im Dialog; die Eckdaten ermittelt die Software
 * vor (Vorbelegung vom Server). In den Stammdaten steht nur der Abschnitt Antrag offen, ohne eigene Überschrift, die übrigen kommen
 * über den Pfeil am Fuß der Kachel – sonst schöben sie den ELiA-Baum darunter weit nach unten.
 *
 * Die Komponente hält keinen Stand. Jede Eingabe geht an `onChange`, der Abschluss einer Eingabe
 * zusätzlich an `onCommit` – beim Verlassen eines Textfelds, beim Schließen einer
 * Mehrfachauswahl, mit der Auswahl in einem einwertigen Feld bzw. im Kalender. Die Stammdaten
 * speichern dort (Blur-Pattern, docs/adr/004_autosave_dirty_check.md), das Anlegen braucht es
 * nicht.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import {
  AssignmentTurnedInOutlined as AssignmentTurnedInIcon,
  TuneOutlined as TuneIcon,
} from "@mui/icons-material";
import Dropdown from "../../../common/inputs/Dropdown";
import InputField from "../../../common/inputs/InputField";
import InputFieldDate from "../../../common/inputs/InputFieldDate";
import MultiSelectDropdown from "../../../common/inputs/MultiSelectDropdown";
import ExpandableDetails from "../../../layout/ExpandableDetails";
import SubSection, { subSectionFullWidth } from "../../../layout/SubSection";
import { colors } from "../../../../styles/theme";
import { permitsApi } from "../../../../services/api/permits";
import { OrgUser } from "../../../../services/api/users";
import {
  AnlageOption,
  LookupOption,
  PermitProcedureReference,
  ProcedureTypeOption,
  StandortmerkmalOption,
  UvpStatusOption,
  WindenergiegebietOption,
} from "../../../../types";
import { ANTRAG_FELDER, uvpOptionen } from "./antragsFelder";
import {
  ANDERE_PROJEKTE_ANZEIGEN,
  bezugsantragOptionen,
} from "./bezugsantragOptionen";

/** Die Angaben eines Antrags, wie die Felder sie zeigen – leere Zeichenkette = kein Wert. */
export interface AntragFeldWerte {
  procedureTypeId: string;
  bezeichnung: string;
  /** IAM-ID der Bearbeitung */
  bearbeitungUserId: string;
  bezugsgenehmigungId: string;
  zusatzantragBemerkung: string;
  beschreibung: string;
  /** Antragsgegenstand – die Anlagen, die der Antrag umfasst */
  anlageIds: string[];
  vorhabenbestandteilIds: string[];
  uvpStatusId: string;
  windenergiegebietId: string;
  standortmerkmalIds: string[];
  /** ISO-Daten */
  eingereichtAm: string;
  vollstaendigSeit: string;
  genehmigungGeplantAm: string;
  /** Antragskonferenz am – nur beim Anlegen ein Feld hier, danach im Unterabschnitt */
  conferencePlannedOn: string;
  bescheidVom: string;
  aktenzeichen: string;
}

export type AntragFeld = keyof AntragFeldWerte;

export type AntragFeldHandler = <F extends AntragFeld>(
  feld: F,
  wert: AntragFeldWerte[F],
) => void;

/** ELiA kreuzt in 1.1.3 nur bei diesen beiden Antragsarten einen Zusatzantrag an. */
const ZUSATZANTRAG_ARTEN = ["TEILGENEHMIGUNG", "VORZEITIGER_BEGINN"];

interface AntragFelderProps {
  /** Anlegen zeigt nur, was beim Anlegen schon feststeht; Stammdaten alles */
  variante: "anlegen" | "stammdaten";
  werte: AntragFeldWerte;
  onChange: AntragFeldHandler;
  /** Abschluss einer Eingabe – in den Stammdaten der Speicherzeitpunkt */
  onCommit?: AntragFeldHandler;
  fehler?: Partial<Record<AntragFeld, string>>;
  disabled?: boolean;
  procedureTypeOptions: ProcedureTypeOption[];
  uvpStatusOptions: UvpStatusOption[];
  vorhabenbestandteilOptions: LookupOption[];
  /** Die Anlagen des Projekts */
  anlageOptionen: AnlageOption[];
  windenergiegebietOptions: WindenergiegebietOption[];
  standortmerkmalOptions: StandortmerkmalOption[];
  /** Benutzer des Mandanten – Auswahl für die Bearbeitung */
  orgUsers: OrgUser[];
  /** Projekt des Antrags – gruppiert die Bezugsanträge nach eigenem und fremdem Projekt */
  projectId?: string;
  /** Der Antrag selbst – er kann nicht auf sich selbst verweisen */
  eigeneId?: string;
  /** Nur Stammdaten: der Unterabschnitt Antragskonferenz, offen unter dem Abschnitt Antrag */
  konferenz?: React.ReactNode;
}

const AntragFelder: React.FC<AntragFelderProps> = ({
  variante,
  werte,
  onChange,
  onCommit,
  fehler = {},
  disabled = false,
  procedureTypeOptions,
  uvpStatusOptions,
  vorhabenbestandteilOptions,
  anlageOptionen,
  windenergiegebietOptions,
  standortmerkmalOptions,
  orgUsers,
  projectId,
  eigeneId,
  konferenz,
}) => {
  const stammdaten = variante === "stammdaten";

  /** Einwertige Auswahl: die Änderung ist zugleich der Abschluss der Eingabe. */
  const waehle: AntragFeldHandler = (feld, wert) => {
    onChange(feld, wert);
    onCommit?.(feld, wert);
  };
  /** Abschluss ohne neuen Wert – Verlassen eines Feldes, Schließen eines Menüs. */
  const schliesseAb: AntragFeldHandler = (feld, wert) => onCommit?.(feld, wert);

  // Die Liste kommt einmal vollständig und wird im Client gruppiert (bezugsantragOptionen).
  const [referenzen, setReferenzen] = useState<PermitProcedureReference[]>([]);
  useEffect(() => {
    let verworfen = false;
    permitsApi.getProcedureReferences().then((geladen) => {
      if (!verworfen) setReferenzen(geladen);
    });
    return () => {
      verworfen = true;
    };
  }, []);
  const [andereProjekte, setAndereProjekte] = useState(false);
  const bezug = useMemo(
    () =>
      bezugsantragOptionen(
        referenzen,
        projectId,
        eigeneId,
        andereProjekte,
        werte.bezugsgenehmigungId,
      ),
    [
      referenzen,
      projectId,
      eigeneId,
      andereProjekte,
      werte.bezugsgenehmigungId,
    ],
  );

  const benutzerOptionen = useMemo(
    () => orgUsers.map((u) => ({ value: u.user_id, label: u.name || u.email })),
    [orgUsers],
  );

  const zeigeZusatzantragBemerkung =
    stammdaten &&
    ZUSATZANTRAG_ARTEN.includes(
      procedureTypeOptions.find((option) => option.id === werte.procedureTypeId)
        ?.code ?? "",
    );

  /** Datumsfeld: `onChange` meldet beim Tippen jeden Zwischenstand, `onCommit` den Abschluss. */
  const datumsfeld = (
    feld:
      | "eingereichtAm"
      | "vollstaendigSeit"
      | "genehmigungGeplantAm"
      | "conferencePlannedOn"
      | "bescheidVom",
    required = false,
  ) => (
    <InputFieldDate
      variant="floating"
      label={ANTRAG_FELDER[feld].label}
      value={werte[feld]}
      onChange={(wert) => onChange(feld, wert ?? "")}
      onCommit={(wert) => schliesseAb(feld, wert ?? "")}
      readOnly={disabled}
      required={required}
      error={fehler[feld]}
      tooltip={ANTRAG_FELDER[feld].tooltip}
      labelBackgroundColor={colors.background.paper}
    />
  );

  const anlegen = !stammdaten;

  // Anlagen und Geplante Genehmigung sind nur beim Anlegen Pflicht: Später darf ein Antrag ohne
  // sie bestehen. Ohne Anlagen im Projekt gäbe es nichts zu wählen – dann auch dort nicht.
  const anlagenPflicht = anlegen && anlageOptionen.length > 0;

  // Antragsart steht nicht als Feld hier: Sie wird beim Anlegen vorab gewählt und steht danach
  // nur noch im Titel – geändert wird sie nicht mehr. Beschreibung und Bezugsantrag pflegt man
  // erst in den Stammdaten.
  const antrag = (
    <SubSection
      title="Antrag"
      card={anlegen}
      icon={<AssignmentTurnedInIcon />}
      // In den Stammdaten steht der Abschnitt direkt unter dem Kacheltitel „Stammdaten“ –
      // eine zweite Überschrift darunter wäre nur eine Zeile mehr ohne Information.
      hideTitle={stammdaten}
      expandable={false}
      columns={anlegen ? 3 : 2}
      // In den Stammdaten folgt der Pfeil direkt, der Abstand zum nächsten Abschnitt entsteht
      // erst beim Aufklappen.
      sx={stammdaten ? { mb: 0 } : undefined}
    >
      <InputField
        label={ANTRAG_FELDER.bezeichnung.label}
        value={werte.bezeichnung}
        onChange={(wert) => onChange("bezeichnung", wert)}
        onBlur={(wert) => schliesseAb("bezeichnung", wert)}
        placeholder="z. B. BImSchG-Antrag Bauabschnitt Nord"
        readOnly={disabled}
        labelBackgroundColor={colors.background.paper}
        required
        error={fehler.bezeichnung}
        tooltip={ANTRAG_FELDER.bezeichnung.tooltip}
      />
      {/* Pflicht, weil die Wiedervorlagen dieses Antrags daran hängen. */}
      <Dropdown
        variant="floating"
        label={ANTRAG_FELDER.bearbeitungUserId.label}
        value={werte.bearbeitungUserId}
        options={benutzerOptionen}
        searchable
        onChange={(wert) => waehle("bearbeitungUserId", wert)}
        disabled={disabled}
        required
        error={!!fehler.bearbeitungUserId}
        helperText={fehler.bearbeitungUserId}
        tooltip={ANTRAG_FELDER.bearbeitungUserId.tooltip}
      />
      {datumsfeld("genehmigungGeplantAm", anlegen)}
      {/* Freiwillig und unauffällig: Sie soll beim Anlegen nur nicht vergessen werden. */}
      {anlegen && datumsfeld("conferencePlannedOn")}
      {stammdaten && (
        <Dropdown
          variant="floating"
          label={ANTRAG_FELDER.bezugsgenehmigungId.label}
          value={werte.bezugsgenehmigungId}
          options={bezug.optionen}
          searchable
          listenAktion={
            bezug.kannErweitern
              ? {
                  label: ANDERE_PROJEKTE_ANZEIGEN,
                  onClick: () => setAndereProjekte(true),
                }
              : undefined
          }
          onChange={(wert) => waehle("bezugsgenehmigungId", wert)}
          disabled={disabled}
          tooltip={ANTRAG_FELDER.bezugsgenehmigungId.tooltip}
        />
      )}
      {stammdaten && (
        <Box sx={subSectionFullWidth}>
          <InputField
            label={ANTRAG_FELDER.beschreibung.label}
            value={werte.beschreibung}
            onChange={(wert) => onChange("beschreibung", wert)}
            onBlur={(wert) => schliesseAb("beschreibung", wert)}
            placeholder="optional"
            readOnly={disabled}
            labelBackgroundColor={colors.background.paper}
            tooltip={ANTRAG_FELDER.beschreibung.tooltip}
          />
        </Box>
      )}
      {zeigeZusatzantragBemerkung && (
        <Box sx={subSectionFullWidth}>
          <InputField
            label={ANTRAG_FELDER.zusatzantragBemerkung.label}
            value={werte.zusatzantragBemerkung}
            onChange={(wert) => onChange("zusatzantragBemerkung", wert)}
            onBlur={(wert) => schliesseAb("zusatzantragBemerkung", wert)}
            placeholder="optional"
            readOnly={disabled}
            labelBackgroundColor={colors.background.paper}
            tooltip={ANTRAG_FELDER.zusatzantragBemerkung.tooltip}
          />
        </Box>
      )}
    </SubSection>
  );

  const eckdaten = (
    <SubSection
      title="Eckdaten"
      card={anlegen}
      icon={<TuneIcon />}
      expandable={false}
      columns={2}
      sx={anlegen ? { mb: 4 } : undefined}
    >
      {/* Pflicht nur beim Anlegen: Später ist ein Antrag ohne Anlage unvollständig, nicht
          ungültig – das Löschen einer Anlage darf nie an einem Antrag scheitern. */}
      <MultiSelectDropdown
        label={ANTRAG_FELDER.anlageIds.label}
        values={werte.anlageIds}
        options={anlageOptionen.map((anlage) => ({
          value: anlage.id,
          label: anlage.bezeichnung,
        }))}
        onChange={(ids) => onChange("anlageIds", ids)}
        onClose={(ids) => schliesseAb("anlageIds", ids)}
        disabled={disabled}
        required={anlagenPflicht}
        error={!!fehler.anlageIds}
        placeholder={
          anlageOptionen.length === 0
            ? "Das Projekt hat noch keine Anlagen"
            : "keine"
        }
        helperText={
          fehler.anlageIds ??
          (stammdaten &&
          werte.anlageIds.length === 0 &&
          anlageOptionen.length > 0
            ? "Ohne Anlage ist der Antrag unvollständig"
            : undefined)
        }
        tooltip={ANTRAG_FELDER.anlageIds.tooltip}
      />
      <MultiSelectDropdown
        label={ANTRAG_FELDER.vorhabenbestandteilIds.label}
        values={werte.vorhabenbestandteilIds}
        options={vorhabenbestandteilOptions.map((option) => ({
          value: option.id,
          label: option.name,
        }))}
        onChange={(ids) => onChange("vorhabenbestandteilIds", ids)}
        onClose={(ids) => schliesseAb("vorhabenbestandteilIds", ids)}
        disabled={disabled}
        required
        error={!!fehler.vorhabenbestandteilIds}
        helperText={fehler.vorhabenbestandteilIds}
        tooltip={ANTRAG_FELDER.vorhabenbestandteilIds.tooltip}
      />
      <Dropdown
        variant="floating"
        label={ANTRAG_FELDER.uvpStatusId.label}
        value={werte.uvpStatusId}
        options={uvpOptionen(uvpStatusOptions)}
        onChange={(wert) => waehle("uvpStatusId", wert)}
        disabled={disabled}
        required
        error={!!fehler.uvpStatusId}
        helperText={fehler.uvpStatusId}
        tooltip={ANTRAG_FELDER.uvpStatusId.tooltip}
      />
      <Dropdown
        variant="floating"
        label={ANTRAG_FELDER.windenergiegebietId.label}
        value={werte.windenergiegebietId}
        options={windenergiegebietOptions.map((gebiet) => ({
          value: gebiet.id,
          label: gebiet.name,
          tooltip: gebiet.description,
        }))}
        onChange={(wert) => waehle("windenergiegebietId", wert)}
        disabled={disabled}
        required
        error={!!fehler.windenergiegebietId}
        helperText={fehler.windenergiegebietId}
        tooltip={ANTRAG_FELDER.windenergiegebietId.tooltip}
      />
      <MultiSelectDropdown
        label={ANTRAG_FELDER.standortmerkmalIds.label}
        tooltip={ANTRAG_FELDER.standortmerkmalIds.tooltip}
        values={werte.standortmerkmalIds}
        options={standortmerkmalOptions.map((merkmal) => ({
          value: merkmal.id,
          label: merkmal.name,
          description: merkmal.description,
        }))}
        onChange={(ids) => onChange("standortmerkmalIds", ids)}
        onClose={(ids) => schliesseAb("standortmerkmalIds", ids)}
        disabled={disabled}
        placeholder="keine"
      />
    </SubSection>
  );

  if (anlegen) {
    return (
      <Box>
        {antrag}
        {eckdaten}
      </Box>
    );
  }

  // Was erst im Verfahren entsteht – nur in den Stammdaten. Paarweise: Eingang und
  // Vollständigkeit, aus denen die Wiedervorlagen rechnen, dann der Bescheid. Das Aktenzeichen
  // vergibt die Behörde mit dem Eingang; es ist zugleich das, worauf ein Folgeantrag Bezug nimmt.
  const verfahren = (
    <SubSection title="Verfahren" expandable={false} columns={2} sx={{ mb: 0 }}>
      {datumsfeld("eingereichtAm")}
      {datumsfeld("vollstaendigSeit")}
      {datumsfeld("bescheidVom")}
      <InputField
        label={ANTRAG_FELDER.aktenzeichen.label}
        value={werte.aktenzeichen}
        onChange={(wert) => onChange("aktenzeichen", wert)}
        onBlur={(wert) => schliesseAb("aktenzeichen", wert)}
        placeholder="vergibt die Behörde mit dem Eingang"
        readOnly={disabled}
        labelBackgroundColor={colors.background.paper}
        tooltip={ANTRAG_FELDER.aktenzeichen.tooltip}
      />
    </SubSection>
  );

  return (
    <ExpandableDetails
      details={
        <Box sx={{ pt: 5 }}>
          {eckdaten}
          {verfahren}
        </Box>
      }
    >
      {antrag}
      {konferenz}
    </ExpandableDetails>
  );
};

export default AntragFelder;
